import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api } from '../lib/api';
import { Heart, Loader2, Image as ImageIcon, Film } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CreationLightbox from '../components/jixxeo/CreationLightbox';

/**
 * Discover — SWR-style per-tab cache + background refresh.
 * Top-level split: Pictures | Videos. Each pane has its own set of sub-tabs
 * (For You · Trending · New · Following). Switching tabs is zero-fetch after
 * the first visit.
 */
const MEDIA_TABS = [
  { key: 'image', label: 'Pictures', icon: ImageIcon },
  { key: 'video', label: 'Videos',   icon: Film },
];
const SUB_TABS = [
  { key: 'for_you',   label: 'For You' },
  { key: 'trending',  label: 'Trending' },
  { key: 'new',       label: 'New' },
  { key: 'following', label: 'Following', protected: true },
];
const STALE_MS = 60_000;
const CACHE_VERSION = 'v4-slim';
const CACHE_KEY = `jixxeo_discover_cache_${CACHE_VERSION}`;

function loadCache() {
  try {
    // Nuke old-version caches so clients from older builds don't see
    // a stale "No pictures yet" when the backend actually has content.
    Object.keys(sessionStorage).forEach(k => {
      if (k.startsWith('jixxeo_discover_cache_') && k !== CACHE_KEY) sessionStorage.removeItem(k);
    });
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
  } catch { return {}; }
}
function saveCache(cache) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch {}
}
const cacheKey = (media, sub) => `${media}::${sub}`;

export default function Discover() {
  const { user } = useAuth();
  const [media, setMedia] = useState('image');   // 'image' | 'video'
  const [sub, setSub] = useState('for_you');
  const cacheRef = useRef(loadCache());
  const [items, setItems] = useState(() => cacheRef.current[cacheKey('image', 'for_you')]?.items || []);
  const [loading, setLoading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const persistCache = useCallback(() => saveCache(cacheRef.current), []);

  const fetchTab = useCallback(async (mediaK, subK, { force = false } = {}) => {
    const k = cacheKey(mediaK, subK);
    const cached = cacheRef.current[k];
    const fresh = cached && (Date.now() - cached.fetched_at < STALE_MS);
    if (cached && !force) {
      if (mediaK === media && subK === sub) setItems(cached.items);
      if (fresh) return;
    } else if (mediaK === media && subK === sub) {
      setLoading(true);
    }
    try {
      const { data } = await api.get(`/feed?tab=${subK}&type=${mediaK}`);
      cacheRef.current[k] = { items: data || [], fetched_at: Date.now() };
      persistCache();
      if (mediaK === media && subK === sub) setItems(data || []);
    } catch {
      // Keep cached data visible on transient errors
    } finally {
      if (mediaK === media && subK === sub) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, sub, persistCache]);

  // Render cached data instantly on tab change, revalidate in the background
  useEffect(() => {
    const cached = cacheRef.current[cacheKey(media, sub)];
    setItems(cached?.items || []);
    fetchTab(media, sub, { force: !cached });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, sub]);

  // Prefetch neighbouring tabs on idle for instant switching
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = idle(() => {
      for (const m of MEDIA_TABS) {
        for (const s of SUB_TABS) {
          if (s.protected && !user) continue;
          if (m.key === media && s.key === sub) continue;
          if (cacheRef.current[cacheKey(m.key, s.key)]) continue;
          fetchTab(m.key, s.key).catch(() => {});
        }
      }
    });
    return () => cancel(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, media, sub]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      const cached = cacheRef.current[cacheKey(media, sub)];
      if (!cached || Date.now() - cached.fetched_at > STALE_MS) fetchTab(media, sub);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [media, sub, fetchTab]);

  const like = async (id) => {
    try {
      await api.post(`/creations/${id}/like`);
      const bump = (list) => list.map(i => i.id === id ? { ...i, likes: (i.likes || 0) + 1 } : i);
      setItems(bump(items));
      Object.keys(cacheRef.current).forEach(k => {
        if (cacheRef.current[k]?.items) cacheRef.current[k].items = bump(cacheRef.current[k].items);
      });
      persistCache();
    } catch {}
  };

  const visibleSubs = SUB_TABS.filter(t => !t.protected || !!user);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold">Discover</h1>
        <p className="text-sm text-slate-400">Explore trending AI creations</p>
      </div>

      {/* Media type split */}
      <Tabs value={media} onValueChange={setMedia}>
        <TabsList data-testid="discover-media-tabs" className="glass">
          {MEDIA_TABS.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`media-tab-${t.key}`} className="gap-1.5">
                <Icon className="w-4 h-4" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Sub-tabs */}
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList data-testid="discover-tabs" className="glass">
          {visibleSubs.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key.replace('_', '-')}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading && items.length === 0 && (
        <Card data-testid="discover-loading" className="glass p-10 text-center">
          <Loader2 className="w-5 h-5 mx-auto animate-spin text-violet-400" />
          <div className="text-xs text-slate-400 mt-2">Loading {media === 'image' ? 'pictures' : 'videos'}…</div>
        </Card>
      )}

      {!loading && items.length === 0 && (
        <Card className="glass p-10 text-center">
          <div className="font-display text-lg font-bold">
            {media === 'image' ? 'No pictures yet' : 'No videos yet'}
          </div>
          <div className="text-sm text-slate-400 mt-1">Be the first to publish!</div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((c, i) => (
          <Card key={c.id} data-testid={`feed-${c.id}`} className="glass overflow-hidden group">
            <button
              data-testid={`feed-open-${c.id}`}
              onClick={() => setLightboxIdx(i)}
              className="aspect-square bg-black w-full relative cursor-zoom-in"
              aria-label="Open creation"
            >
              {c.type === 'image' && c.url && (
                <img src={c.url} alt={c.prompt || ''} loading="lazy" decoding="async"
                     className="w-full h-full object-cover" />
              )}
              {c.type === 'video' && c.url && (
                <video src={c.url} muted loop autoPlay playsInline preload="metadata"
                       poster={c.poster_url}
                       className="w-full h-full object-cover" />
              )}
            </button>
            <div className="p-3">
              <div className="text-xs text-slate-400 line-clamp-2 mb-2">{c.prompt}</div>
              <div className="flex items-center justify-between text-xs">
                <div className="text-slate-500">@{c.creator?.username}</div>
                <button data-testid={`like-${c.id}`} onClick={() => like(c.id)}
                        className="flex items-center gap-1 text-pink-400">
                  <Heart className="w-3.5 h-3.5" /> {c.likes || 0}
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {lightboxIdx >= 0 && (
        <CreationLightbox
          items={items}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onLike={like}
        />
      )}
    </div>
  );
}
